import React, { createContext, useContext, useState, useEffect, useRef } from 'react';

const CompareContext = createContext();
const STORAGE_KEY = 'sunify_compare';
const MAX_COMPARE = 4;

export const useCompare = () => {
  const ctx = useContext(CompareContext);
  if (!ctx) throw new Error('useCompare must be used within CompareProvider');
  return ctx;
};

const safeRead = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};

export const CompareProvider = ({ children }) => {
  const [productIds, setProductIds] = useState(safeRead);
  const isFirstRun = useRef(true);

  useEffect(() => {
    // Skip the very first effect run so we don't immediately overwrite localStorage
    // (productIds is already initialized FROM localStorage on first render).
    if (isFirstRun.current) {
      isFirstRun.current = false;
      return;
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(productIds));
  }, [productIds]);

  const has = (id) => productIds.includes(id);

  const toggle = (id) => {
    setProductIds((prev) => {
      if (prev.includes(id)) return prev.filter((p) => p !== id);
      if (prev.length >= MAX_COMPARE) return prev; // ignore over-limit
      return [...prev, id];
    });
  };

  const remove = (id) => {
    setProductIds((prev) => prev.filter((p) => p !== id));
  };

  const clear = () => setProductIds([]);

  return (
    <CompareContext.Provider value={{ productIds, has, toggle, remove, clear, max: MAX_COMPARE }}>
      {children}
    </CompareContext.Provider>
  );
};
